import apiClient from './apiClient';

/**
 * Cache für die Mietrenditen-Statistik (RenditeStatistikDTO) mit Time-to-Live.
 * Reduziert redundante API-Aufrufe bei Navigation, Tab-Wechsel und Jahreswechsel.
 *
 * Pattern analog zu anlageVCacheService.js:
 * - TTL 15min (Renditeberechnung ist teuer; manueller Refresh via #statistik-refresh-button)
 * - Explizite Invalidierung nach Mutationen (Property, PropertyGroup, ObjectCost, Loan, Booking)
 * - ETag-Support (304 Not Modified)
 * - localStorage-Persistenz (überlebt Page-Reload)
 * - Request-Deduplizierung (Cache-Stampede-Schutz)
 *
 * WICHTIG: Importiert nur apiClient — kein Import von anderen Services,
 * um Importzyklen zu vermeiden. Andere Services importieren dieses Modul.
 */
const CACHE_VERSION = 'v1';
const CACHE_PREFIX = `statistik:${CACHE_VERSION}:`;
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 Minuten

// In-Memory Cache als Fallback (Private Mode, localStorage nicht verfügbar)
const memoryCache = new Map();
const pendingRequests = new Map();

// Invalidation-Token für Race-Condition-Schutz bei clearCache()
let cacheInvalidationToken = 0;

/**
 * Prüft ob localStorage verfügbar ist.
 * @returns {boolean}
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__statistik_storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

/**
 * AP-C Finding 10: Ein Cache-Eintrag gilt nur als frisch, wenn sein Timestamp NICHT
 * in der Zukunft liegt (Schutz gegen manipuliertes localStorage oder einen Uhrensprung,
 * der einen Eintrag sonst beliebig lange als „frisch" erscheinen ließe) und innerhalb
 * der TTL liegt.
 */
const isFresh = (entry) => {
  if (!entry || typeof entry.timestamp !== 'number') return false;
  const age = Date.now() - entry.timestamp;
  return age >= 0 && age < CACHE_TTL_MS;
};

/**
 * AP-C Finding 10: grobe Shape-Prüfung eines Cache-Eintrags — verwirft Einträge mit
 * unplausibler Struktur (z. B. durch localStorage-Manipulation), statt sie als gültig
 * zu behandeln. `rows` muss (falls vorhanden) ein Array sein, `summary` (falls vorhanden)
 * ein Objekt oder null.
 * @param {*} entry - geparster localStorage-Eintrag
 * @returns {boolean}
 */
const isValidEntryShape = (entry) => {
  if (!entry || typeof entry !== 'object') return false;
  if (typeof entry.timestamp !== 'number') return false;
  const { data } = entry;
  if (data !== null && data !== undefined) {
    if (typeof data !== 'object') return false;
    if (data.rows !== undefined && !Array.isArray(data.rows)) return false;
    if (data.summary !== undefined && data.summary !== null && typeof data.summary !== 'object') return false;
  }
  return true;
};

/**
 * Liest Cache-Eintrag aus localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @returns {object|null} Cache-Eintrag oder null wenn nicht vorhanden/ungültig
 */
const getFromLocalStorage = (key) => {
  if (!storageAvailable) return null;
  try {
    const item = localStorage.getItem(CACHE_PREFIX + key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    if (!isValidEntryShape(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Schreibt Cache-Eintrag in localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @param {object} value - Cache-Wert { data, timestamp, etag }
 */
const setToLocalStorage = (key, value) => {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota exceeded — Fallback auf Memory-Cache
  }
};

const removeFromLocalStorage = (key) => {
  if (!storageAvailable) return;
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch {
    // Ignorieren bei Fehlern
  }
};

const invalidateCacheKey = (key) => {
  memoryCache.delete(key);
  pendingRequests.delete(key);
  removeFromLocalStorage(key);
};

/**
 * Gibt eine flache Kopie des gecachten RenditeStatistikDTO zurück, inklusive
 * kopiertem rows-Array (mit kopierten Row-Objekten), damit Aufrufer den
 * Cache-Eintrag nicht versehentlich mutieren können.
 * @param {object} data - Gecachtes DTO
 * @returns {object} Kopie
 */
const cloneDto = (data) => {
  if (!data || typeof data !== 'object') return data;
  return {
    ...data,
    rows: Array.isArray(data.rows) ? data.rows.map((row) => ({ ...row })) : data.rows,
    hinweise: Array.isArray(data.hinweise) ? [...data.hinweise] : data.hinweise,
    summary: data.summary && typeof data.summary === 'object' ? { ...data.summary } : data.summary
  };
};

const wrapSharedPromiseForCaller = (sharedPromise, signal) => {
  if (!signal) {
    return sharedPromise;
  }

  if (signal.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      signal.removeEventListener('abort', handleAbort);
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      reject(abortError);
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    sharedPromise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

/**
 * Löscht alle Cache-Einträge aus localStorage.
 */
const clearLocalStorageCache = () => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren bei Fehlern
  }
};

/**
 * Generische Cache-Funktion für GET-Requests auf den /api/statistik-Endpoint.
 * Unterstützt localStorage-Persistenz und ETag-basierte Revalidierung.
 * @param {string} url - API-Pfad
 * @param {object} [params] - Query-Parameter
 * @param {AbortSignal} [signal] - AbortController-Signal
 * @param {number} [_retryCount=0] - Interner Retry-Zähler
 * @returns {Promise<object>} RenditeStatistikDTO
 */
const getCached = (url, params, signal, _retryCount = 0) => {
  // Frühzeitig abbrechen wenn Signal bereits abgebrochen
  if (signal?.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  const cacheKey = params ? `${url}?${JSON.stringify(params)}` : url;

  // 1. Prüfe Memory-Cache (schnellster Pfad)
  const memoryCached = memoryCache.get(cacheKey);
  if (isFresh(memoryCached)) {
    return Promise.resolve(cloneDto(memoryCached.data));
  }
  if (memoryCached) {
    memoryCache.delete(cacheKey);
  }

  // 2. Prüfe localStorage-Cache
  const localCached = getFromLocalStorage(cacheKey);
  if (isFresh(localCached)) {
    memoryCache.set(cacheKey, localCached);
    return Promise.resolve(cloneDto(localCached.data));
  }
  if (localCached) {
    removeFromLocalStorage(cacheKey);
  }

  // 3. Deduplizierung: laufenden Request wiederverwenden
  if (pendingRequests.has(cacheKey)) {
    return wrapSharedPromiseForCaller(
      pendingRequests.get(cacheKey),
      signal
    );
  }

  // 4. API-Request mit ETag-Support
  const config = {
    validateStatus: (status) => status === 200 || status === 304
  };
  if (params) config.params = params;
  if (signal) config.signal = signal;

  // ETag senden falls vorhanden (Revalidierung)
  const staleEntry = localCached || memoryCached;
  const etag = staleEntry?.etag;
  if (etag) {
    config.headers = { 'If-None-Match': etag };
  }

  const currentToken = cacheInvalidationToken;

  // Promise-Referenz merken, damit im finally nur dann aus
  // pendingRequests gelöscht wird, wenn es noch unser eigener Eintrag ist.
  const promise = apiClient.get(url, config).then(response => {
    // RACE-SCHUTZ: Nicht cachen wenn clearCache()/invalidate aufgerufen wurde.
    if (currentToken !== cacheInvalidationToken) {
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      if (response.status === 304) {
        // AP-C Finding 9: Eine 304-Antwort bezieht sich auf ein If-None-Match, das VOR der
        // zwischenzeitlichen Invalidierung erzeugt wurde — staleEntry.data darf hier NICHT
        // mehr als aktuell gelten (der Cache wurde ja gerade wegen einer Mutation geleert).
        // Statt der veralteten Daten frisch nachladen (ohne ETag, da bereits invalidiert).
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        // Retry-Limit erreicht: keine veralteten Daten zurückgeben. Als Abbruch behandeln —
        // die Aufrufer (StatistikPage) ignorieren AbortError ohnehin und laden bei Bedarf neu.
        const abortError = new Error('Request aborted');
        abortError.name = 'AbortError';
        throw abortError;
      }
      // 200 OK: echte, frische Serverantwort — inhaltlich vertrauenswürdig, wird aber wegen
      // der zwischenzeitlichen Invalidierung bewusst nicht (erneut) gecacht.
      return cloneDto(response.data);
    }

    if (response.status === 304) {
      // 304 Not Modified: Cached Daten nutzen, Timestamp aktualisieren
      if (!staleEntry) {
        if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
        invalidateCacheKey(cacheKey);
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return null;
      }

      // Abbruch-Schutz: nicht cachen wenn Signal abgebrochen
      if (signal?.aborted) {
        if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
        return cloneDto(staleEntry.data);
      }

      const refreshedEntry = {
        data: staleEntry.data,
        timestamp: Date.now(),
        etag: staleEntry.etag
      };
      memoryCache.set(cacheKey, refreshedEntry);
      setToLocalStorage(cacheKey, refreshedEntry);
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      return cloneDto(staleEntry.data);
    }

    // Abbruch-Schutz: nicht cachen wenn Signal abgebrochen (200 OK)
    if (signal?.aborted) {
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      return cloneDto(response.data);
    }

    // 200 OK: Neue Daten cachen
    const data = response.data;
    const responseEtag = response.headers['etag'];
    const newEntry = {
      data,
      timestamp: Date.now(),
      etag: responseEtag
    };
    memoryCache.set(cacheKey, newEntry);
    setToLocalStorage(cacheKey, newEntry);
    if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
    return cloneDto(data);
  }).catch(err => {
    if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
    // Axios-Abbruch (CanceledError / ERR_CANCELED) sauber behandeln: kein Cache vergiften
    if (err && (err.name === 'CanceledError' || err.code === 'ERR_CANCELED')) {
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      throw abortError;
    }
    throw err;
  });

  pendingRequests.set(cacheKey, promise);

  return wrapSharedPromiseForCaller(promise, signal);
};

const statistikCacheService = {
  /**
   * Lädt oder berechnet die Mietrenditen-Statistik für ein Jahr (gecacht, 15min TTL).
   *
   * @param {number|string} year - Statistikjahr (1900..2100)
   * @param {AbortSignal} [signal] - AbortController-Signal
   * @returns {Promise<object>} RenditeStatistikDTO { year, rows[], summary, hinweise[] }
   */
  getRendite: (year, signal) => getCached('/statistik/rendite', { year }, signal),

  /**
   * Invalidiert alle Statistik-Caches (nach Mutationen der Datenquellen).
   * Wird aufgerufen von: propertyCacheService, objektkostenService, loanCacheService,
   * bookingService, PropertyForm.js und der Statistik-Seite selbst (Inline-Erfassung).
   */
  invalidateAll: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Löscht den kompletten Cache (bei Logout).
   */
  clearCache: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  }
};

export default statistikCacheService;
